import { MAX_BOARDS, MAX_FILE_BYTES, MAX_TEXT_FILE_BYTES, normalizeBoard, normalizePack, parseTextBoard, newBoard } from './BoardPack.mjs';

import { originalIndex, isOriginal } from './OriginalIds.mjs';
export { originalId, isOriginal } from './OriginalIds.mjs';

export const LIBRARY_KEY = 'ladderjs-library-v4';
export const MAX_REVISIONS = 200;
export const MAX_SETS = 20;
export const ORIGINAL_SET = 'original';
export const makeId = kind => `${kind}-${crypto.randomUUID()}`;
const validId = id => typeof id === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(id) && id !== ORIGINAL_SET;

function nameOf(value, label) {
    if (typeof value !== 'string' || !value.trim() || value.trim().length > 60) throw new Error(`${label}: use a name of 1–60 characters.`);
    return value.trim();
}

// Storage and full library backups allow unfinished maps. Playable exports and
// ordinary board imports require valid game data. Both paths
// validate identity and references so a set can never silently change its route.
export function validateLibrary(value, drafts = false) {
    if (!value || value.version !== 4 || !Array.isArray(value.boards) || !Array.isArray(value.sets)) throw new Error('Unsupported library format or version.');
    if (value.boards.length > MAX_BOARDS || value.sets.length > MAX_SETS) throw new Error(`A library can hold ${MAX_BOARDS} custom boards and ${MAX_SETS} level sets.`);
    const ids = new Set();
    const revisionIds = new Set();
    const draftData = board => {
        if (typeof board.name !== 'string' || !Array.isArray(board.layout) || !board.layout.every(row => typeof row === 'string')) throw new Error('Invalid saved board draft.');
        return { name: board.name, layout: [...board.layout] };
    };
    const boards = value.boards.map((board, index) => {
        if (!board || !validId(board.id) || isOriginal(board.id) || ids.has(board.id)) throw new Error('Board IDs must be unique.');
        ids.add(board.id);
        const data = drafts ? draftData(board) : normalizeBoard(board, index);
        const history = board.revisions ?? [];
        if (!Array.isArray(history)) throw new Error('Invalid revision history.');
        let lastNumber = 0;
        const revisions = history.map(revision => {
            if (!revision || !validId(revision.id) || revisionIds.has(revision.id) || !Number.isSafeInteger(revision.number) || revision.number <= lastNumber) throw new Error('Invalid revision identity or order.');
            revisionIds.add(revision.id); lastNumber = revision.number;
            return { id: revision.id, number: revision.number, ...draftData(revision) };
        });
        return { id: board.id, ...data, revisions };
    });
    if (revisionIds.size > MAX_REVISIONS) throw new Error(`The library can hold ${MAX_REVISIONS} saved revisions. Export a backup before removing unused boards.`);
    const setIds = new Set();
    const sets = value.sets.map(set => {
        if (!set || !validId(set.id) || setIds.has(set.id)) throw new Error('Level-set IDs must be unique.');
        setIds.add(set.id);
        const name = nameOf(set.name, 'Level set');
        if (!Array.isArray(set.boardIds) || set.boardIds.length > MAX_BOARDS) throw new Error(`A level set can contain at most ${MAX_BOARDS} levels.`);
        if (!set.boardIds.every(id => ids.has(id) || isOriginal(id))) throw new Error(`“${name}” refers to a board that is missing.`);
        const pins = set.revisionIds;
        if (!Array.isArray(pins) || pins.length !== set.boardIds.length || !pins.every((pin, i) => isOriginal(set.boardIds[i]) ? pin === null : boards.find(board => board.id === set.boardIds[i]).revisions.some(revision => revision.id === pin))) throw new Error(`“${name}” refers to a missing or incorrect board revision.`);
        return { id: set.id, name, boardIds: [...set.boardIds], revisionIds: [...pins] };
    });
    return { version: 4, boards, sets,
        selectedBoardId: ids.has(value.selectedBoardId) ? value.selectedBoardId : boards[0]?.id,
        selectedSetId: setIds.has(value.selectedSetId) ? value.selectedSetId : ORIGINAL_SET,
        campaignId: setIds.has(value.campaignId) ? value.campaignId : ORIGINAL_SET };
}

export function loadLibrary(storage) {
    const saved = storage.getItem(LIBRARY_KEY);
    if (saved !== null) return validateLibrary(JSON.parse(saved), true);
    return validateLibrary({ version: 4, boards: [{ id: makeId('board'), ...newBoard(), revisions: [] }], sets: [] }, true);
}

export function saveLibrary(storage, library) {
    storage.setItem(LIBRARY_KEY, JSON.stringify(validateLibrary(library, true)));
}

export function resolveBoard(library, id, originals) {
    return isOriginal(id) ? originals[originalIndex(id)] : library.boards.find(board => board.id === id);
}

export function resolveEntry(library, set, index, originals) {
    const board = resolveBoard(library, set.boardIds[index], originals);
    if (isOriginal(set.boardIds[index])) return board;
    return board?.revisions?.find(revision => revision.id === set.revisionIds[index]);
}

export function saveRevision(library, boardId) {
    if (isOriginal(boardId)) throw new Error('Original boards are permanently locked. Make a custom copy instead.');
    const board = library.boards.find(item => item.id === boardId);
    if (!board) throw new Error('Choose a custom board.');
    const data = normalizeBoard(board);
    const last = board.revisions?.at(-1);
    if (last && sameBoard(last, data)) return last;
    if (library.boards.reduce((total, item) => total + (item.revisions?.length || 0), 0) >= MAX_REVISIONS) throw new Error(`The library can hold ${MAX_REVISIONS} saved revisions.`);
    const revision = { id: makeId('revision'), number: (last?.number || 0) + 1, ...data };
    (board.revisions ??= []).push(revision);
    return revision;
}

export function sameBoard(a, b) {
    try { return JSON.stringify(normalizeBoard(a)) === JSON.stringify(normalizeBoard(b)); }
    catch { return a.name === b.name && JSON.stringify(a.layout) === JSON.stringify(b.layout); }
}

export function adoptRevision(library, setId, index, revisionId) {
    if (setId === ORIGINAL_SET) throw new Error('The original campaign is permanently locked.');
    const set = library.sets.find(item => item.id === setId);
    if (isOriginal(set?.boardIds[index])) throw new Error('Original boards cannot have revisions, including in a custom Series.');
    const board = library.boards.find(item => item.id === set?.boardIds[index]);
    const revision = board?.revisions?.find(item => item.id === revisionId);
    if (!revision) throw new Error('This revision does not belong to the selected Series entry.');
    normalizeBoard(revision);
    set.revisionIds[index] = revision.id;
}

export function resolveSet(library, id, originals) {
    if (id === ORIGINAL_SET) return originals.map(normalizeBoard);
    const set = library.sets.find(item => item.id === id);
    if (!set) throw new Error('Choose an existing level set.');
    if (!set.boardIds.length) throw new Error(`“${set.name}” has no levels. Add boards from the library first.`);
    return set.boardIds.map((boardId, index) => {
        const board = resolveEntry(library, set, index, originals);
        if (!board) throw new Error(`Level ${index + 1} refers to a missing board.`);
        try { return normalizeBoard(board, index); }
        catch (error) { throw new Error(`“${board.name || 'Untitled'}”: ${error.message}`); }
    });
}

export function removeBoard(library, id) {
    if (isOriginal(id)) throw new Error('Original boards cannot be deleted.');
    const users = library.sets.filter(set => set.boardIds.includes(id));
    if (users.length) throw new Error(`Remove this board from these level sets first: ${users.map(set => set.name).join(', ')}.`);
    return { ...library, boards: library.boards.filter(board => board.id !== id) };
}

export function parseLibraryImport(text, filename = 'Imported set.json') {
    const txt = /\.txt$/i.test(filename);
    if (!txt && !/\.json$/i.test(filename)) throw new Error('Only .txt boards and .json collections are accepted.');
    const bytes = new TextEncoder().encode(text).length;
    if (bytes > (txt ? MAX_TEXT_FILE_BYTES : MAX_FILE_BYTES)) throw new Error(txt ? 'Text boards are limited to 2 KB (2,048 bytes).' : 'JSON collections are limited to 1 MB.');
    if (txt) return { boards: [parseTextBoard(text, filename)], sets: [], kind: 'board' };
    let data;
    try { data = JSON.parse(text); } catch { throw new Error('This is not valid JSON.'); }
    if (data?.format === 'ladderjs-library' || data?.format === 'ladderjs-level-set') {
        const drafts = data.format === 'ladderjs-library';
        const library = validateLibrary(data, true);
        if (!drafts) for (const set of library.sets) set.boardIds.forEach((id, i) => { if (!isOriginal(id)) normalizeBoard(resolveEntry(library, set, i, [])); });
        return { boards: library.boards, sets: library.sets, kind: 'library', drafts };
    }
    const boards = normalizePack(data).map(board => ({ id: makeId('board'), ...board }));
    const single = !Array.isArray(data) && Array.isArray(data?.layout);
    return { boards, sets: single ? [] : [{ id: makeId('set'), name: filename.replace(/\.json$/i, '').trim().slice(0, 60) || 'Imported set', boardIds: boards.map(board => board.id) }], kind: single ? 'board' : 'set' };
}

// Build and validate the whole result before returning it. Imported IDs are
// remapped so re-importing files cannot overwrite boards used by existing sets.
export function mergeImport(library, imported, createSet = false) {
    library = validateLibrary(library, true);
    const mapping = new Map();
    const revisionMapping = new Map();
    const boards = imported.boards.map(board => {
        const id = makeId('board');
        if (board.id) mapping.set(board.id, id);
        const data = imported.drafts || board.revisions ? { name: board.name, layout: [...board.layout] } : normalizeBoard(board);
        const revisions = (board.revisions || []).map(revision => {
            const revisionId = makeId('revision'); revisionMapping.set(revision.id, revisionId);
            return { ...revision, id: revisionId, layout: [...revision.layout] };
        });
        return { id, ...data, revisions };
    });
    const pin = id => isOriginal(id) ? null : saveRevision({ boards }, id).id;
    const sets = imported.sets.map(set => {
        const boardIds = set.boardIds.map(id => isOriginal(id) ? id : mapping.get(id));
        return { id: makeId('set'), name: set.name, boardIds, revisionIds: set.revisionIds ? set.revisionIds.map(id => id === null ? null : revisionMapping.get(id)) : boardIds.map(pin) };
    });
    if (createSet && !sets.length && boards.length) sets.push({ id: makeId('set'), name: boards[0].name, boardIds: boards.map(board => board.id), revisionIds: boards.map(board => pin(board.id)) });
    const result = validateLibrary({ ...library, boards: [...library.boards, ...boards], sets: [...library.sets, ...sets],
        selectedBoardId: boards[0]?.id || library.selectedBoardId,
        selectedSetId: sets[0]?.id || library.selectedSetId,
        campaignId: createSet && sets.length ? sets[0].id : library.campaignId }, true);
    return result;
}

export function exportLibrary(library, setId) {
    library = validateLibrary(library, true);
    let boards = library.boards, sets = library.sets;
    if (setId !== undefined) {
        const set = sets.find(item => item.id === setId);
        if (!set) throw new Error('Select a custom level set to export.');
        if (!set.boardIds.length) throw new Error('Add boards before exporting this level set.');
        sets = [set];
        boards = boards.filter(board => set.boardIds.includes(board.id));
    }
    if (setId !== undefined) for (const set of sets) set.boardIds.forEach((id, i) => { if (!isOriginal(id)) normalizeBoard(resolveEntry(library, set, i, [])); });
    const data = validateLibrary({ version: 4, boards, sets }, true);
    const text = JSON.stringify({ format: setId === undefined ? 'ladderjs-library' : 'ladderjs-level-set', version: 4, boards: data.boards, sets: data.sets }, null, 2) + '\n';
    if (new TextEncoder().encode(text).length > MAX_FILE_BYTES) throw new Error('This backup exceeds the 1 MB import limit. Export individual Series instead.');
    return text;
}
